import {
  Box,
  Container,
  Button,
  Collapse,
  Stack,
  Tooltip,
} from "@mui/material";
import React, { useCallback, useState } from "react";
import BackBtn from "../../widgets/BackBtn";
import QueueStatsLive from "./QueueStatsLive";
import StatusStatsLive from "./StatusStatsLive";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { FiBarChart2, FiEye, FiEyeOff } from "react-icons/fi";
import useApiEp from "../../hooks/useApiEp";
import DataTable from "../../widgets/DataTable";
import RenderStatusMessage from "../../widgets/RenderStatusMessage";
import RdCampaignCheck from "./RdCampaignCheck";
import { Square } from "lucide-react";
import ScheduledInfo from "./ScheduledInfo";
import TitleActionsContainer from "../../widgets/TitleActionsContainer";
import PageTitle from "../../widgets/PageTitle";
import SmartStatusButton from "../../widgets/SmartStatusButton";
import axios from "axios";
import { API_ENDPOINT } from "../../utils/constants";
import { getHeader, toastError } from "../../utils/util";
import { GlassButton } from "../../themes/UiElements";
import { LoadingButton } from "@mui/lab";

const RdMyCampaignLive = () => {
  const [searchParams] = useSearchParams();
  const rcsType = searchParams.get("type");
  const { id } = useParams();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(30);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [stopping, setStopping] = useState(false);
  const navigate = useNavigate();
  const { data, isLoading, isError, error } = useApiEp(
    `/campaign/get-campaign-report/${id}?page=${page}&limit=${limit}&type=${
      rcsType ? rcsType : ""
    }`
  );
  const {
    data: campaignData,
    isLoading: isCampaignLoading,
    isError: isCampaignError,
    error: campaignError,
  } = useApiEp(`/campaign/campaign/${id}`);
  const handleAction = useCallback((action, id) => {
    switch (action) {
      case "view":
        navigate(`/home/${id}/message-detail?isRcs=${rcsType === "rcs"}`);
        break;
      default:
        console.warn("unknown action", id);
    }
  }, []);

  const updateCampaignState = useCallback(
    async (status) => {
      if (!id) {
        return;
      }
      try {
        setUpdatingStatus(true);
        const result = await axios.post(
          `${API_ENDPOINT}/campaign/update-campaign-state`,
          { id, status },
          getHeader()
        );
      } catch (error) {
        toastError(error);
      } finally {
        setUpdatingStatus(false);
      }
    },
    [id]
  );
  const handlePause = useCallback(
    () => updateCampaignState("pause"),
    [updateCampaignState]
  );
  const handleResume = useCallback(
    () => updateCampaignState("resume"),
    [updateCampaignState]
  );
  const handleStopCampaign = useCallback(
    () => updateCampaignState("stop"),
    [updateCampaignState]
  );

  return (
    <Container sx={{ mt: 4, mb: 6 }} maxWidth="lg">
      <BackBtn />
      <TitleActionsContainer>
        <PageTitle>Campaign Report</PageTitle>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          sx={{
            width: { xs: "100%", sm: "auto" },
            mt: { xs: 1, sm: 0 },
          }}
        ></Stack>
      </TitleActionsContainer>
      {campaignData &&
        campaignData.status === "scheduled" &&
        campaignData.category === "scheduled" && (
          <Box>
            <ScheduledInfo data={campaignData} />
          </Box>
        )}

      <Box
        sx={{
          bgcolor: "background.paper",
          borderRadius: "16px",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
          p: 4,
          mb: 2,
          mt: 2,
        }}
      >
        {campaignData && (
          <SmartStatusButton
            status={campaignData.status}
            onPause={handlePause}
            onResume={handleResume}
          />
        )}
        {campaignData?.status === "scheduled" && (
          <LoadingButton
            variant="outlined"
            color="error"
            loading={updatingStatus}
            startIcon={<Square />}
            sx={{ ml: 2 }}
            onClick={handleStopCampaign}
          >
            Stop
          </LoadingButton>
        )}
        <QueueStatsLive id={id} />
        <Box sx={{ mb: 2 }}>
          <StatusStatsLive id={id} type={rcsType} />
        </Box>
        <RdCampaignCheck id={id} />
        <Box sx={{ textAlign: "end" }} mb={2}>
          <Link
            to={
              rcsType === "rcs"
                ? `/home/my-campaigns?isRcs=true`
                : `/home/my-campaigns`
            }
            style={{
              color: "#1976d2",
              textDecoration: "none",
              fontWeight: 500,
              "&:hover": { textDecoration: "underline" },
            }}
          >
            Go to Campaign Reports
          </Link>
        </Box>
        <DataTable
          sx={{ mb: 1 }}
          columns={[
            {
              id: "serial",
              label: "S.No.",
              render: (_v, _row, idx) => (page - 1) * limit + idx + 1,
            },
            { id: "campaignName", label: "Campaign" },
            { id: "to", label: "Recipient" },
            {
              id: "status",
              label: "Status",
              render: (v) => <RenderStatusMessage status={v} />,
            },
            {
              id: "createdOn",
              label: "Created On",
              render: (v) =>
                new Date(v).toLocaleString("en-IN", {
                  dateStyle: "medium",
                }),
            },
          ]}
          actions={["view"]}
          rows={data?.data ?? []}
          page={page}
          limit={limit}
          totalPages={data?.totalPages ?? 1}
          onPageChange={setPage}
          onAction={handleAction}
        />
      </Box>
    </Container>
  );
};

export default RdMyCampaignLive;
